import type { ContentPack, Item } from '@ab/content-schema';
import { resolveFor } from '../combat.ts';
import { lookup } from '../content.ts';
import { type EffectSource, applyEffect, assertEscapeAllowed, assertRunnable, assertSomeoneToHeal } from '../effects.ts';
import { type Dir, isDir } from '../grid.ts';
import { assertLeaderMayAct, faceUnit, runRound } from '../round.ts';
import { InvalidCommand, type RunState, takeOneFromBag, unitById } from '../state.ts';
import { wakeUp } from '../status.ts';
import { type ApplyResult, type CommandHandler, type Ctx, type Step, chain, none } from './types.ts';

/**
 * The leader faces `dir` (if given) and uses a consumable from the bag, then
 * the round runs. The item's `use` must list the `dungeon` context. `target`
 * is required for targeting shape `single` and forbidden otherwise. Items
 * always hit (no accuracy roll) and never charge; a `consumed` item leaves
 * the bag (one from the first stack holding it).
 */
export type UseItemCommand = {
  readonly type: 'use_item';
  readonly item: string;
  readonly dir?: Dir;
  readonly target?: string;
};

type DungeonUse = NonNullable<Item['use']> & { readonly targeting: NonNullable<NonNullable<Item['use']>['targeting']> };

/**
 * The item's dungeon use, or throws: it must be in the bag, not a charm (a
 * charm acts by itself and is never used by hand), usable in a dungeon, with
 * runnable effects.
 */
const dungeonUse = (ctx: Ctx, state: RunState, itemId: string): DungeonUse => {
  if (!state.bag.some((e) => e.item === itemId && e.count > 0)) throw new InvalidCommand('item_not_in_bag', `${itemId} is not in the bag`, { item: itemId });
  const item = lookup(ctx.content.items, itemId);
  if (item?.category === 'charm') throw new InvalidCommand('item_acts_by_itself', `the ${itemId} acts by itself`, { item: itemId });
  const use = item?.use;
  if (use === undefined || !use.contexts.includes('dungeon') || use.targeting === undefined) {
    throw new InvalidCommand('item_not_usable_in_dungeon', `${itemId} cannot be used in a dungeon`, { item: itemId });
  }
  assertRunnable(use.effects, null);
  return { ...use, targeting: use.targeting };
};

/**
 * Whether the item could be used now, before any targeting: the checks of
 * `use_item` up to and excluding targeting (in the bag, not a charm, usable
 * in a dungeon, runnable effects). Returns the `InvalidCommand` message, or null when those
 * checks pass. A client asks this before prompting for a direction or a
 * target; `apply` still checks the whole command.
 */
export const itemUsability = (content: ContentPack, state: RunState, itemId: string): string | null => {
  try {
    dungeonUse({ content }, state, itemId);
    return null;
  } catch (e) {
    if (e instanceof InvalidCommand) return e.message;
    throw e;
  }
};

/**
 * `item_used` with the targets, the item leaves the bag if consumed, then for
 * each target in order: the hit lands (a sleeper wakes, status.ts `wakeUp`),
 * then every effect.
 */
const useItemAction = (ctx: Ctx, state: RunState, userId: string, itemId: string, use: DungeonUse, target: string | null): ApplyResult => {
  const targets = resolveFor(state, unitById(state, userId), use.targeting, target);
  const source: EffectSource = { unit: userId, style: null, element: null, falloffPower: 0, origin: 'item' };
  const used: ApplyResult = {
    state: use.consumed ? takeOneFromBag(state, itemId) : state,
    events: [{ type: 'item_used', unit: userId, item: itemId, targets: targets.map((t) => t.unit) }],
  };
  return chain(
    used.state,
    (s) => ({ state: s, events: used.events }),
    ...targets.flatMap((rt): Step[] => [
      (s) => (s.phase === 'playing' ? wakeUp(s, rt.unit) : none(s)),
      ...use.effects.map((e): Step => (s) => applyEffect(ctx, s, e, source, rt)),
    ]),
  );
};

export const useItem: CommandHandler<UseItemCommand> = (ctx, state, command) => {
  const leader = assertLeaderMayAct(state, 'other');
  // Logs arrive as JSON, so the payload is checked at run time too.
  if (typeof command.item !== 'string') throw new InvalidCommand('item_not_an_id', 'item must be an item id');
  if (command.dir !== undefined && !isDir(command.dir)) throw new InvalidCommand('bad_direction', 'bad direction');
  if (command.target !== undefined && typeof command.target !== 'string') throw new InvalidCommand('target_not_unit_id', 'target must be a unit id');
  const use = dungeonUse(ctx, state, command.item);
  const facing = command.dir ?? leader.facing;
  const target = command.target ?? null;
  const targets = resolveFor(state, { ...leader, facing }, use.targeting, target);
  assertEscapeAllowed(state, leader.id, targets, use.effects);
  assertSomeoneToHeal(ctx, state, targets, use.effects);
  return chain(
    state,
    (s) => faceUnit(s, leader.id, facing),
    (s) => runRound(ctx, s, (x) => useItemAction(ctx, x, leader.id, command.item, use, target), 'busy'),
  );
};
